import type { HDNodeWallet } from "ethers";
import type {
  VipWeeklyRewardEmailReminderPreference,
} from "~~/shared/atelier";
import {
  extractVipWeeklyRewardEmailReminderPreference,
  normalizeVipWeeklyRewardEmailReminderPreference,
} from "~~/shared/utils/vip-reminder.utils";
import type { UserPayload } from "~~/shared/types/user.interfaces";

export const useUserController = () => {
  const userCache = useUserCache();
  const userStore = useUserStore();
  const appStateStore = useAppStateStore();
  const { updateKeys } = useAuthController();
  const { ensureRegisteredRescuePublicKey } = useRescueController();
  const { reCaptchaToken, execute } = useRecaptcha();
  const { kycUrl } = useRuntimeConfig().public;
  const { $gfalIdApi, $serverApi } = useNuxtApp();
  const { web3Sign } = useWeb3();
  const { generateWallet, getPrivateKey } = useWeb3();

  const getUserWallet = async () => {
    const userResponse = await $serverApi<GFALResponse<UserPayload>>(
      "/api/users/self",
      {
        headers: constructHeaders(userStore.authToken),
      }
    ).catch((error) => {
      console.error("error", error);
      return error;
    });
    if (userResponse.payload) userStore.setWallet(userResponse.payload);
    return userResponse;
  };

  const ensureUserHasNoWallet = async () =>
  {
    const walletResponse = await getUserWallet();
    if (walletResponse.statusCode !== 200) {
      throw Object.assign(new Error("Failed to verify existing wallet"), {
        statusCode: walletResponse.statusCode || 500,
        strStatusCode: walletResponse.strStatusCode || "ERROR",
      });
    }

    if (walletResponse.payload?.walletAddress) {
      throw Object.assign(new Error("Wallet already exists"), {
        statusCode: 409,
        strStatusCode: "CONFLICT",
      });
    }
  };

  const formatBalance = (balance: number) => {
    const [integerPart, decimalPart] = balance.toString().split(".");
    return Number(
      `${integerPart ? integerPart : "0"}.${
        decimalPart ? decimalPart.slice(0, 2) : "00"
      }`
    );
  };

  const getUserBalance = async () => {
    try {
      const bscBalance = await useWeb3().fetchBalance();
      const formattedBalance = formatBalance(bscBalance.GFAL);
      userStore.balance = formattedBalance;
      return {
        statusCode: 200,
        strStatusCode: "OK",
        payload: { GFAL: formattedBalance, USD: Number(bscBalance.USD) },
        message: "Balance fetched from BSCScan",
      };
    } catch (error) {
      useLogger().warn(
        "BSCScan API failed, falling back to server API:",
        error
      );

      const response = await $serverApi<GFALResponse<Balance>>(
        "/api/users/self/balance",
        {
          headers: constructHeaders(userStore.authToken),
        }
      ).catch((error) => {
        useLogger().error("balance error", error);
        return error;
      });
      if (response.payload) {
        userStore.balance = formatBalance(response.payload.GFAL);
      }
      return response;
    }
  };

  const getUserDevices = async () => {
    const response = await $gfalIdApi<GFALResponse<UserDevices | null>>(
      `/api/users/self/devices`,
      {
        headers: {
          Authorization: `Bearer ${userStore.authToken}`,
        },
      }
    ).catch((error) => {
      console.error("error", error);
      return error;
    });
    return response.payload;
  };

  const deleteUserDevice = async (deviceId: string) => {
    const response = await $gfalIdApi<GFALResponse<UserDevices | null>>(
      `/api/users/self/devices/${deviceId}`,
      {
        method: "DELETE",
        headers: constructHeaders(userStore.authToken),
      }
    ).catch((error) => {
      console.error("error", error);
      return error;
    });
    return response.payload;
  };

  const deleteUserDevices = async (deviceId: string) => {
    const response = await $gfalIdApi<GFALResponse<UserDevices | null>>(
      `/api/users/self/devices?id=${deviceId}`,
      {
        method: "DELETE",
        headers: constructHeaders(userStore.authToken),
      }
    ).catch((error) => {
      console.error("error", error);
      return error;
    });
    return response.payload;
  };

  const verifyPassword = async (password: string) => {
    const response = await $gfalIdApi<GFALResponse<any>>(
      "/api/auth/verify/password",
      {
        method: "POST",
        headers: constructHeaders(userStore.authToken),
        body: { password },
      }
    ).catch((error) => {
      console.error("error", error);
      return error;
    });
    return response;
  };

  const getOwnCollection = async (
    merchantId: string,
    page: number = 1,
    pageSize: number = 10,
    filters: string = "&sort=RECEIVED_TIME&order=DESC"
  ) => {
    try {
      const response = await $serverApi<GFALResponse<Collectibles | null>>(
        `/api/users/${userStore.id}/collectibles?merchantId=${merchantId}&page=${page}&pageSize=${pageSize}${filters}`,
        {
          headers: constructHeaders(userStore.authToken),
        }
      );
      return (
        response.payload || { total: 0, records: [], page: 0, totalPages: 0 }
      );
    } catch (error) {
      console.error("error", error);
      return { total: 0, records: [], page: 0, totalPages: 0 };
    }
  };

  const getUserCollection = async (
    merchantId: string,
    gfalId: string,
    page: number = 1,
    pageSize: number = 10,
    filters: string = ""
  ) => {
    const response = await $serverApi<GFALResponse<Collectibles | null>>(
      `/api/users/${gfalId}/collectibles?merchantId=${merchantId}&page=${page}&pageSize=${pageSize}${filters}`,
      {
        headers: constructHeaders(userStore.authToken),
      }
    ).catch((error) => {
      console.error("error", error);
      return {
        statusCode: 500,
        strStatusCode: error.message || "Internal Server Error",
        payload: null,
        message: error.message || "An error occurred",
      };
    });
    if (!response.payload || response.statusCode !== 200)
      return { total: 0, records: [], page: 0, totalPages: 0 };
    return response.payload;
  };

  const getUserCollectibleById = async (collectibleId: string) => {
    try {
      const response = await $serverApi<GFALResponse<Collectible | null>>(
        `/api/collectibles/${collectibleId}`,
        {
          headers: constructHeaders(userStore.authToken),
        }
      );
      return response;
    } catch (error: any) {
      if (error?.statusCode === 404 || error?.response?.status === 404) {
        console.log(`User collectible lookup for ${collectibleId} returned 404; treating as expected for GFAL-owned items.`);
        return null;
      }
      console.error("error", error);
      return null;
    }
  };

  const getUserOffers = async (
    agent: "BUYER" | "SELLER",
    page: number = 1,
    pageSize: number = 5
  ) => {
    const response = await $serverApi<GFALResponse<OfferPayload>>(
      `/api/users/self/offers`,
      {
        headers: constructHeaders(userStore.authToken),
        query: {
          groupBy: "COLLECTIBLE",
          agent,
          page,
          pageSize,
        },
      }
    ).catch((error) => {
      console.error("error", error);
      return error;
    });
    return response;
  };

  const getUserCollectionFilters = async (
    gfalId: string,
    merchantId: string,
    propertyType: string[]
  ) => {
    try {
      const response = await $serverApi<GFALResponse<FiltersPayload | null>>(
        `/api/users/${gfalId}/collectibles/filters`,
        {
          headers: constructHeaders(userStore.authToken),
          query: {
            merchantId,
            propertyType: propertyType.join(","),
          },
        }
      ).catch((error) => {
        console.error("error", error);
        return error;
      });
      if (!response) return null;
      return response.payload;
    } catch (error) {
      console.error("error", error);
      return null;
    }
  };

  const getUserMints = async (merchantId?: string) => {
    const response = await $serverApi<GFALResponse<Mint[]>>(
      "/api/users/self/mints",
      {
        headers: constructHeaders(userStore.authToken),
        ...(merchantId && { query: { merchantId } }),
      }
    ).catch((error) => {
      console.error("error", error);
      return error;
    });
    return response.payload;
  };

  const getUserMintById = async (mintId: string) => {
    const response = await $serverApi<GFALResponse<Mint>>(
      `/api/users/self/mints/${mintId}`,
      {
        headers: constructHeaders(userStore.authToken),
      }
    ).catch((error) => {
      console.error("error", error);
      return error;
    });
    return response ? response.payload : null;
  };

  const createMint = async (identifier: MintIdentifier) => {
    const mintIdentifier = identifier;
    return await $serverApi<GFALResponse<Purchase>>(
      `/api/users/self/mints/${mintIdentifier.id}/purchases`,
      {
        method: "POST",
        headers: constructHeaders(userStore.authToken),
        body: {
          collectibleId: mintIdentifier.id,
        },
      }
    );
  };

  const signMint = async (
    identifier: MintIdentifier,
    purchaseId: string | undefined,
    operationHash: string,
    code: string | undefined
  ) => {
    const mintId = identifier.id;
    const signature = await web3Sign(operationHash);
    return await $serverApi<GFALResponse<Purchase>>(
      `/api/users/self/mints/${mintId}/sign`,
      {
        method: "POST",
        headers: constructHeaders(userStore.authToken),
        body: {
          signature,
          code,
        },
      }
    );
  };

  const cancelMint = async (mintId: string) => {
    const response = await $serverApi<GFALResponse<Mint>>(
      `/api/users/self/mints/${mintId}`,
      {
        method: "DELETE",
        headers: constructHeaders(userStore.authToken),
      }
    ).catch((error) => {
      console.error("error", error);
      return error;
    });
    return response;
  };

  const setUserWalletInServer = async (wallet: HDNodeWallet) => {
    const response = await $serverApi<GFALResponse<UserPayload>>(
      "/api/users/self/wallet",
      {
        method: "POST",
        headers: constructHeaders(userStore.authToken, {
          "X-Humanity-Token": reCaptchaToken.value,
        }),
        body: {
          walletAddress: wallet.address,
        },
      }
    );
    if (response.payload) userStore.setWallet(response.payload);
    return response;
  };

  const encryptPrivateKeyWithMFA = async ({
    value,
    privateKey,
  }: {
    value: string;
    privateKey: string;
  }): Promise<UserKey> => {
    return {
      value: await encrypt(privateKey, value),
      time: new Date().toISOString(),
    };
  };

  const createRescueKey = async (
    privateKey: string,
    rescuePublicKey: string
  ): Promise<UserKey> => {
    return {
      value: await encryptAsymmetric(privateKey, rescuePublicKey),
      time: new Date().toISOString(),
    };
  };

  const generateKeys = async (
    password: string | null,
    method: UserKeyMethod,
    value: string,
    privateKey: string
  ): Promise<UserKeys> => {
    let keys = userStore.keys;
    if (!keys)
      keys = {
        password: null,
        recoveryPassword: null,
        google: null,
        apple: null,
        facebook: null,
        rescue: null,
        privateKey: null,
      };
    let passwordEncryptedKeys: UserKey | null = null;
    if (password) {
      passwordEncryptedKeys = await encryptPrivateKeyWithMFA({
        value: password,
        privateKey,
      });
      keys.password = passwordEncryptedKeys;
    }
    const encryptedKey = await encryptPrivateKeyWithMFA({
      value,
      privateKey,
    });
    keys[method] = encryptedKey;
    return keys;
  };

  const updatePasswordWalletKey = async (
    newPassword: string,
    privateKey?: string
  ) => {
    if (!privateKey) privateKey = await getPrivateKey();
    if (userStore.keys) {
      const newPasswordKey = await encryptPrivateKeyWithMFA({
        value: newPassword,
        privateKey,
      });
      userStore.keys.password = newPasswordKey;
      useWeb3().setPrivateKey(privateKey);
      return await updateKeys(userStore.keys);
    }
  };

  const createWallet = async ({
    password,
    method,
    value,
  }: {
    password: string | null;
    method: UserKeyMethod;
    value: string;
  }) => {
    try {
      await ensureUserHasNoWallet();
      const rescuePublicKey = await ensureRegisteredRescuePublicKey();
      await execute("WALLET_CREATION");
      const wallet = generateWallet();
      const keys = await generateKeys(
        password,
        method,
        value,
        wallet.privateKey
      );
      keys.rescue = await createRescueKey(wallet.privateKey, rescuePublicKey);
      await updateKeys(keys);
      const walletResponse = await setUserWalletInServer(wallet);
      return walletResponse;
    } catch (error: any) {
      console.error("Error creating wallet:", error);
      return {
        statusCode: error.statusCode || 500,
        strStatusCode: error.strStatusCode || "ERROR",
        payload: null,
        message: error?.message || "Failed to create wallet",
      };
    }
  };

  const hasKeysOtherThanPrivate = (keys: UserKeys): boolean => {
    return Object.keys(keys).some(
      (key) => key !== "privateKey" && keys[key as keyof UserKeys] !== null
    );
  };

  const updateWalletKeys = async ({
    method,
    value,
    password,
  }: {
    method: UserKeyMethod;
    value: string;
    password: string | null;
  }) => {
    try {
      const privateKey = await getPrivateKey();
      const rescuePublicKey = await ensureRegisteredRescuePublicKey();
      useWeb3().setPrivateKey(privateKey);
      const keys = await generateKeys(password, method, value, privateKey);
      keys.rescue = await createRescueKey(privateKey, rescuePublicKey);
      if (hasKeysOtherThanPrivate(keys) && keys.privateKey)
        keys.privateKey = null;
      return await updateKeys(keys);
    } catch (error: any) {
      console.error("Error updating wallet keys:", error);
      return {
        statusCode: error.statusCode || 500,
        strStatusCode: error.strStatusCode || "ERROR",
        payload: null,
        message: error?.message || "Failed to create wallet",
      };
    }
  };

  const setUserWalletUpdateComplete = () => {
    userStore.requireWalletUpdate = false;
  };

  const getTransactions = async (
    page: number,
    pageSize: number
  ): Promise<{
    transactions: TransactionHistoryPayload;
    statusCode: number;
    message?: string;
  }> => {
    const {
      public: { debugMode },
    } = useRuntimeConfig();
    const shouldDebugActivity = import.meta.dev || Boolean(debugMode);

    const [pendingResponse, response] = await Promise.all([
      page === 1 ? getPendingTransactions() : Promise.resolve({ payload: [] }),
      $serverApi<GFALResponse<TransactionHistoryPayload | null>>(
        "/api/users/self/transactions",
        {
          headers: constructHeaders(userStore.authToken),
          query: { page, pageSize },
        }
      ).catch((error) => {
        console.error("error", error);
        return {
          statusCode: error.statusCode || 500,
          strStatusCode: error.strStatusCode || "ERROR",
          payload: null,
          message: error.message || "An error occurred",
        };
      }),
    ]);

    if (shouldDebugActivity) {
      console.debug("[activity] transactions response", {
        page,
        pageSize,
        accountAddress: userStore.accountAddress,
        authToken: Boolean(userStore.authToken),
        pendingCount: Array.isArray(pendingResponse.payload)
          ? pendingResponse.payload.length
          : 0,
        statusCode: response.statusCode,
        total: response.payload?.total ?? 0,
        records: response.payload?.records?.length ?? 0,
        merchantIds: [
          ...new Set(
            [
              ...(pendingResponse.payload || []),
              ...(response.payload?.records || []),
            ]
              .map((transaction) => transaction.merchantId)
              .filter((merchantId): merchantId is MerchantId => !!merchantId)
          ),
        ],
      });
    }

    if (response.statusCode !== 200 || !response.payload) {
      return {
        transactions: { records: [], total: 0 },
        statusCode: response.statusCode || 500,
        message: response.message || "Unable to load transactions",
      };
    }

    const userIds = new Set<string>();
    for (const transaction of response.payload.records || []) {
      if (transaction.fromGfalId) userIds.add(transaction.fromGfalId);
      if (transaction.toGfalId) userIds.add(transaction.toGfalId);
    }
    await userCache.addUserIds(Array.from(userIds));

    const transactions = (response.payload.records || []).map(
      (transaction) => ({
        ...transaction,
        status: "COMPLETE" as const,
      })
    );
    const pendingTransactions = (pendingResponse.payload || []).map(
      (transaction: ITransactionItem) => ({
        ...transaction,
        status: "PENDING" as const,
      })
    );
    const transactionMerchantIds = [
      ...new Set(
        [...pendingTransactions, ...transactions]
          .map((transaction) => transaction.merchantId)
          .filter((merchantId): merchantId is MerchantId => !!merchantId)
      ),
    ];
    await Promise.all(
      transactionMerchantIds.map((merchantId) =>
        appStateStore.checkAndGetMerchantTids(merchantId)
      )
    );

    const sumTotal =
      (response.payload?.total || 0) + (pendingTransactions.length || 0);
    const allTransactions: TransactionHistoryPayload = {
      records: [...pendingTransactions, ...transactions],
      total: sumTotal,
    };

    userStore.setTransactions(allTransactions);

    return {
      statusCode: response.statusCode,
      transactions: allTransactions,
    };
  };

  const getPendingTransactions = async () => {
    const response = await $serverApi<GFALResponse<ITransactionItem[] | null>>(
      "/api/users/self/transactions/pending",
      {
        headers: constructHeaders(userStore.authToken),
      }
    ).catch((error) => {
      console.error("error", error);
      return {
        statusCode: error.statusCode || 500,
        strStatusCode: error.strStatusCode || "ERROR",
        payload: null,
        message: error.message || "An error occurred",
      };
    });
    return response;
  };

  const setAntiphishingCode = async (antiphishingCode: string) => {
    const response = await $gfalIdApi<GFALResponse<GFALIDUser | null>>(
      `/api/users/self/antiphishing-code`,
      {
        method: "PUT",
        headers: constructHeaders(userStore.authToken),
        body: {
          antiphishingCode,
        },
      }
    ).catch((error) => {
      console.error("error", error);
      return error;
    });
    if (response.payload)
      userStore.antiphishingCode = response.payload.antiphishingCode;
    return response;
  };

  const setUserName = async (displayName: string, tagline: string) => {
    const response = await $gfalIdApi<GFALResponse<GFALIDUser>>(
      `/api/users/self/username`,
      {
        method: "PUT",
        headers: constructHeaders(userStore.authToken),
        body: {
          displayName,
          tagline,
        },
      }
    ).catch((error) => {
      console.error("error", error);
      return error;
    });
    if (response.payload) userStore.setGfalIdUser(response.payload);
    return response;
  };

  const getUserProfile = async (user: string) => {
    let userProfile = userCache.getUserProfileFromCache(user);
    if (userProfile) {
      return {
        statusCode: 200,
        strStatusCode: "OK",
        payload: userProfile,
        message: "Loaded from cache",
      } as GFALResponse<UserProfile>;
    }

    const response = await $gfalIdApi<GFALResponse<UserProfile | null>>(
      `/api/users/${user}`,
      {
        headers: constructHeaders(userStore.authToken),
      }
    ).catch((error) => {
      console.error("error", error);
      return {
        statusCode: error.statusCode,
        strStatusCode: error.strStatusCode,
        payload: null,
        message: error.message || "An error occurred",
      } as GFALResponse<null>;
    });
    return response;
  };

  const userNameExists = async (
    username: string
  ): Promise<GFALResponse<UserIdAndWallet | null>> => {
    const encodedUsername = encodeURIComponent(username);
    try {
      const response = await $serverApi<GFALResponse<UserIdAndWallet>>(
        `/api/users/${encodedUsername}`,
        {
          headers: constructHeaders(userStore.authToken),
        }
      );

      if (response.statusCode === 200 && response.payload) {
        return response;
      } else if (response.statusCode === 404) {
        return {
          ...response,
          payload: null,
        };
      } else {
        throw new Error(`Unexpected response: ${response.statusCode}`);
      }
    } catch (error) {
      console.error("Error checking username:", error);
      return {
        statusCode: 500,
        strStatusCode: "ERROR",
        payload: null,
        message: "An error occurred while checking the username",
      };
    }
  };

  const verifyKYC = async () => {
    const response = await $serverApi<GFALResponse<KYCPayload>>(
      `${kycUrl}/api/users/self`,
      {
        headers: constructHeaders(userStore.authToken),
      }
    );
    return response;
  };

  const setProfilePicture = async (profilePicture: string) => {
    const response = await $gfalIdApi<GFALResponse<GFALIDUser>>(
      "/api/users/self/profile-picture",
      {
        method: "PUT",
        headers: constructHeaders(userStore.authToken),
        body: {
          profilePicture,
        },
      }
    );
    if (response.payload)
      userStore.profilePicture = response.payload.profilePicture.toUpperCase();
    return response;
  };

  const setProfileBanner = async (profileBanner: string) => {
    const response = await $gfalIdApi<GFALResponse<GFALIDUser>>(
      "/api/users/self/profile-banner",
      {
        method: "PUT",
        headers: constructHeaders(userStore.authToken),
        body: {
          profileBanner,
        },
      }
    );
    if (response.payload)
      userStore.profileBanner = response.payload.profileBanner;
    return response;
  };

  const toggleCommunications = async (communications: boolean) => {
    const response = await $gfalIdApi<GFALResponse<GFALIDUser>>(
      "/api/users/self/communications",
      {
        method: "PUT",
        headers: constructHeaders(userStore.authToken),
        body: {
          communications,
        },
      }
    );
    if (response.payload)
      userStore.communications = response.payload.communications;
    return response;
  };

  const getVipWeeklyRewardEmailReminderPreference = async () => {
    const response = await $serverApi<
      GFALResponse<{
        status: VipWeeklyRewardEmailReminderPreference;
        emailReminderScheduled: boolean;
        emailReminderScheduledAt: string | null;
      }>
    >("/api/users/self/settings/vip-weekly-reward-email-reminder", {
      headers: constructHeaders(userStore.authToken),
    }).catch((error) => {
      console.error("error", error);
      return error;
    });

    if (response?.statusCode === 200 && response?.payload) {
      const preference = extractVipWeeklyRewardEmailReminderPreference(
        response.payload,
      );
      if (preference) {
        userStore.emailReminderPreference = preference;
      }
    }

    return response;
  };

  const isSettingVipWeeklyRewardEmailReminder = ref(false);
  const setVipWeeklyRewardEmailReminder = async (
    enabled: boolean,
    scheduleForClaimId?: string,
  ) => {
    if (isSettingVipWeeklyRewardEmailReminder.value) {
      return {
        statusCode: 429,
        strStatusCode: "IN_PROGRESS",
        payload: null,
        message: "VIP weekly reward reminder update already in progress",
      } satisfies GFALResponse<null>;
    }

    isSettingVipWeeklyRewardEmailReminder.value = true;
    const response = await $serverApi<
      GFALResponse<{
        status: VipWeeklyRewardEmailReminderPreference;
        emailReminderScheduled: boolean;
        emailReminderScheduledAt: string | null;
      }>
    >("/api/users/self/settings/vip-weekly-reward-email-reminder", {
      method: "PUT",
      headers: constructHeaders(userStore.authToken),
      body: {
        enabled,
        ...(scheduleForClaimId ? { scheduleForClaimId } : {}),
      },
    }).catch((error) => {
      if (error?.statusCode !== 400 || !scheduleForClaimId) {
        console.error("error", error);
      }
      return error;
    });

    if (response?.statusCode === 200 && response?.payload) {
      const preference = extractVipWeeklyRewardEmailReminderPreference(
        response.payload,
      );
      if (preference) {
        userStore.emailReminderPreference = preference;
      }
    } else if (response?.statusCode === 200) {
      userStore.emailReminderPreference = enabled ? "enabled" : "disabled";
    }

    isSettingVipWeeklyRewardEmailReminder.value = false;
    return response;
  };

  const unsubscribeCommunications = async (key: string) => {
    const response = await $gfalIdApi<GFALResponse<null>>(
      `/api/keys/unsubscribe-communications`,
      {
        method: "POST",
        headers: constructHeaders(userStore.authToken),
        body: {
          key,
        },
      }
    );
    return response;
  };

  const getNotifications = async (page?: number, pageSize?: number) => {
    const response = await $serverApi<GFALResponse<Notifications>>(
      "/api/users/self/notifications",
      {
        headers: constructHeaders(userStore.authToken),
        query: {
          page,
          pageSize,
        },
      }
    );
    return response;
  };

  const setNotifications = (res: GFALResponse<Notifications>) => {
    if (res.payload) {
      userStore.setNotifications(res.payload);
    }
  };

  const setReadNotification = async (notificationId: string) => {
    const response = await $serverApi<GFALResponse<Notifications>>(
      `/api/users/self/notifications/${notificationId}/read`,
      {
        method: "POST",
        headers: constructHeaders(userStore.authToken),
      }
    );
    return response;
  };

  const setReadAllNotifications = async () => {
    const response = await $serverApi<GFALResponse<Notifications>>(
      "/api/users/self/notifications/read",
      {
        method: "POST",
        headers: constructHeaders(userStore.authToken),
      }
    );
    return response;
  };

  const getUnreadNotificationsCount = async () => {
    const response = await $serverApi<GFALResponse<Notifications>>(
      "/api/users/self/notifications",
      {
        headers: constructHeaders(userStore.authToken),
        query: {
          page: 1,
          pageSize: 1,
        },
      }
    );
    return response;
  };

  const setUnreadNotificationsCount = async (unreadCount: number) => {
    userStore.notifications.unread = unreadCount;
  };

  const setPreferredLocale = async (locale: string) => {
    // TODO: implementation on hold due to sync issues with locale cookie
    const response = await $gfalIdApi<GFALResponse<GFALIDUser>>(
      "/api/users/self/locale",
      {
        method: "PUT",
        headers: constructHeaders(userStore.authToken),
        body: {
          preferredLocale: locale,
        },
      }
    );
    if (response.payload)
      userStore.preferredLocale = response.payload.preferredLocale;
    return response;
  };

  const isSettingBalanceVisibility = ref(false);
  const setBalanceVisibilty = async (balanceVisibility: boolean) => {
    if (isSettingBalanceVisibility.value) return;
    isSettingBalanceVisibility.value = true;
    userStore.showBalance = balanceVisibility;
    const response = await $serverApi<GFALResponse<UserPayload>>(
      "/api/users/self/show-balance",
      {
        method: "PUT",
        headers: constructHeaders(userStore.authToken),
        body: {
          showBalance: balanceVisibility,
        },
      }
    );
    if (!response.payload) userStore.showBalance = !balanceVisibility;
    isSettingBalanceVisibility.value = false;
    return response;
  };

  const sendEmail = async (
    subject: string,
    content: string,
    signal?: AbortSignal
  ) => {
    const sanitizedSubject = subject?.trim() ?? "";
    const sanitizedContent = content?.trim() ?? "";
    return await $serverApi<GFALResponse<null>>(`/api/emails/send`, {
      method: "POST",
      headers: constructHeaders(userStore.authToken, {
        "Content-Type": "application/json",
      }),
      body: {
        emailSubject: sanitizedSubject,
        emailBody: sanitizedContent,
      },
      signal,
    });
  };

  return {
    getUserWallet,
    getUserBalance,
    verifyPassword,
    getOwnCollection,
    getUserCollection,
    getUserCollectibleById,
    getUserOffers,
    getUserCollectionFilters,
    getUserMints,
    getUserMintById,
    createMint,
    signMint,
    cancelMint,
    createWallet,
    updateWalletKeys,
    getTransactions,
    setAntiphishingCode,
    setUserName,
    getUserProfile,
    userNameExists,
    verifyKYC,
    setProfilePicture,
    setProfileBanner,
    toggleCommunications,
    getVipWeeklyRewardEmailReminderPreference,
    setVipWeeklyRewardEmailReminder,
    unsubscribeCommunications,
    getNotifications,
    setNotifications,
    setReadNotification,
    setReadAllNotifications,
    getUnreadNotificationsCount,
    setUnreadNotificationsCount,
    getUserDevices,
    deleteUserDevices,
    deleteUserDevice,
    updatePasswordWalletKey,
    setUserWalletUpdateComplete,
    setPreferredLocale,
    setBalanceVisibilty,
    sendEmail,
  };
};
